import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { LocalizationProvider, MobileDateTimePicker } from "@mui/x-date-pickers";
import MomentUtils from "@date-io/moment";
import moment from "moment";
import { timeConversion } from "client-app-core";
import { TextField } from "@mui/material";
import DateTimePickerActionBar from "./CustomComponents/DateTimePicker/DateTimePickerActionBar";
import HijriUtils from "@date-io/hijri";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { useSelector } from "react-redux";
import { getTranslation } from "orion-components/i18n";

const propTypes = {
	id: PropTypes.string,
	label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	value: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
	handleChange: PropTypes.func.isRequired,
	format: PropTypes.string,
	minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	disabled: PropTypes.bool,
	margin: PropTypes.string,
	options: PropTypes.object,
	customDialogProps: PropTypes.object,
	dir: PropTypes.string,
	okLabel: PropTypes.bool,
	cancelLabel: PropTypes.bool,
	clearLabel: PropTypes.bool,
	clearable: PropTypes.bool,
	locale: PropTypes.string,
	theme: PropTypes.string,
	RenderInput: PropTypes.any,
	getUpdatedDateOnSubmit: PropTypes.bool,
	displaySeconds: PropTypes.bool,
	labelStyle: PropTypes.object
};

const darkThemeSx = {
	"& .MuiPickersDay-dayWithMargin": {
		color: "#fff",
		backgroundColor: "#2C2C2E"
	},
	"& .MuiCalendarPicker-root": {
		color: "#fff",
		backgroundColor: "#2C2C2E"
	},
	"& .MuiTabs-flexContainer": {
		backgroundColor: "#2C2C2E"
	},
	"& .MuiTabs-indicator": {
		border: "1px solid #1688BD",
		backgroundColor: "#1688BD"
	},
	"& .MuiTypography-subtitle1": {
		fontSize: "1.5rem"
	},
	"& .MuiTypography-h3": {
		fontSize: "2rem"
	},
	"& .MuiTypography-h4": {
		fontSize: "1rem"
	},
	"& .MuiPickersToolbar-penIconButton": {
		display: "none !important"
	}
};
const lightThemeSx = {
	"& .MuiTypography-subtitle1": {
		fontSize: "1.5rem"
	},
	"& .MuiTypography-h3": {
		fontSize: "2rem"
	},
	"& .MuiTypography-h4": {
		fontSize: "1rem"
	},
	"& .MuiPickersToolbar-penIconButton": {
		display: "none !important"
	},
	"& .MuiTabs-indicator": {
		border: "1px solid #F50057",
		backgroundColor: "#F50057"
	}
};

const defaultProps = {
	id: "date-time-picker",
	label: "",
	format: "full_12-hour",
	value: null,
	minDate: "1938-01-01",
	maxDate: "2075-01-01",
	disabled: false,
	margin: "normal",
	dir: "ltr",
	locale: "en",
	theme: "dark",
	clearLabel: true,
	okLabel: true,
	cancelLabel: true,
	customDialogProps: {},
	RenderInput: null,
	getUpdatedDateOnSubmit: false,
	labelStyle: {},
	displaySeconds: false
};

const CBDateTimePicker = ({
	id,
	label,
	value,
	handleChange,
	format,
	minDate,
	maxDate,
	disabled,
	margin,
	options,
	customDialogProps,
	dir,
	okLabel,
	cancelLabel,
	clearLabel,
	clearable,
	theme,
	RenderInput,
	getUpdatedDateOnSubmit,
	labelStyle,
	displaySeconds
}) => {
	const locale = useSelector((state) => state.i18n && state.i18n.locale);
	const localeOverride = locale === "ar_kw" ? "en" : locale;

	const styles = {
		inputLabelProps: {
			...(dir === "ltr" && { transformOrigin: "top left" }),
			...(dir === "rtl" && {
				transformOrigin: "top right",
				left: "unset",
				textAlign: "right"
			}),
			...labelStyle
		}
	};

	const formatDateTime = (date, invalidLabel, format) => {
		return date === null
			? ""
			: format && date
				? timeConversion.convertToUserTime(date, format, localeOverride)
				: invalidLabel;
	};

	let clearableValue;
	if (clearable === undefined) {
		clearableValue = true;
	} else {
		clearableValue = clearable;
	}
	moment.locale(localeOverride); // supports date picker localization like calendar date translation and toolbar
	const twelveHourFormat = !format.includes("24-hour") || format.toLowerCase().includes("hh:mm a");

	let tempDate = null;
	const [tab, setTab] = useState("date");
	const [dateTime, setDateTime] = useState(value);
	const [ok, setOk] = useState(false);

	useEffect(() => {
		setDateTime(value);
	}, [value]);

	const resetDateTime = () => {
		if (tempDate === null && dateTime === null) {
			setDateTime(null);
		} else {
			setDateTime(dateTime);
		}
	};

	const setActualDate = () => {
		setOk(!ok);
		tempDate = dateTime;
		handleChange(dateTime);
	};

	const clearTime = () => {
		handleChange(null);
	};

	const actions = [
		clearableValue && clearLabel ? getTranslation("global.CBComponents.DateTime.clear") : null,
		cancelLabel ? getTranslation("global.CBComponents.DateTime.cancel") : null,
		okLabel ? getTranslation("global.CBComponents.DateTime.ok") : null
	];

	return (
		<LocalizationProvider
			dateAdapter={locale === "ar" ? HijriUtils : MomentUtils}
			localeText={localeOverride}
			adapterLocale={localeOverride}
		>
			<MobileDateTimePicker
				id={id || "date-time-picker"}
				{...options}
				label={label}
				closeOnSelect={false}
				value={dateTime}
				onChange={(val) => {
					setDateTime(val);
					if (!getUpdatedDateOnSubmit) {
						handleChange(val);
					}
				}}
				onOpen={() => {
					const date = value === null && dateTime === null ? new Date() : moment(dateTime);
					setDateTime(date);
					handleChange(date);
				}}
				minDate={moment(minDate)}
				showToolbar={true}
				maxDate={moment(maxDate)}
				disabled={disabled}
				{...(displaySeconds ? { views: ["year", "month", "day", "hours", "minutes", "seconds"] } : {})}
				dayOfWeekFormatter={(val) => {
					return (
						<div
							style={{
								color: theme === "dark" ? "#fff" : "black"
							}}
						>
							{val}
						</div>
					);
				}}
				DialogProps={{
					disableEnforceFocus: true,
					sx: theme === "dark" ? darkThemeSx : lightThemeSx,
					style: { direction: "ltr", ...customDialogProps },
					className: theme === "dark" ? "cbDateTimePicker" : "lightcbDateTimePicker"
				}}
				dateRangeIcon={
					<DateRangeIcon
						style={{ color: tab === "date" ? "#ACDAF5" : "#fff" }}
						onClick={() => setTab("date")} data-testid="orion-components-cbcomponents-date-time-cbdate-time-picker-date-range-icon"
					/>
				}
				timeIcon={
					<AccessTimeIcon
						style={{ color: tab === "time" ? "#ACDAF5" : "#fff" }}
						onClick={() => setTab("time")} data-testid="orion-components-cbcomponents-date-time-cbdate-time-picker-access-time-icon"
					/>
				}
				hideTabs={false}
				toolbarTitle={<div />}
				toolbarPlaceholder={null}
				componentsProps={{
					actionBar: {
						actions,
						resetDateTime: resetDateTime,
						submit: setActualDate,
						clearTime: clearTime
					}
				}}
				components={{
					ActionBar: DateTimePickerActionBar
				}}
				ampm={twelveHourFormat} // cSpell:ignore ampm
				renderInput={(params) => {
					params.inputProps.value = formatDateTime(dateTime, "Invalid Date", format);
					if (RenderInput !== null) {
						return <RenderInput dateTime={value} params={params} />;
					} else {
						return (
							<TextField
								{...params}
								{...options}
								defaultValue={null}
								error={value === null ? false : options?.error || params.error}
								helperText={value === null ? false : options?.error || params.error}
								fullWidth
								margin={margin}
								variant="standard"
								InputLabelProps={{
									style: styles.inputLabelProps
								}} data-testid="orion-components-cbcomponents-date-time-cbdate-time-picker-text-field"
							/>
						);
					}
				}} data-testid="orion-components-cbcomponents-date-time-cbdate-time-picker-mobile-date-time-picker"
			/>
		</LocalizationProvider>
	);
};

CBDateTimePicker.propTypes = propTypes;
CBDateTimePicker.defaultProps = defaultProps;

export default CBDateTimePicker;
